import { observer } from "mobx-react"

import { ConfigurableDynamicFormFieldsUnhandledElementUi } from "src/components/ConfigurableDynamicFormFields/components/UnhandledUIElement"
import { ConfigurableDynamicFormFieldsAssetLibraryPicker } from "src/components/ConfigurableDynamicFormFields/formFields/asset-picker"
import { ConfigurableDynamicFormFieldsTextList } from "src/components/ConfigurableDynamicFormFields/formFields/text-list"
import { ConfigurableDynamicFormFieldsTextField } from "src/components/ConfigurableDynamicFormFields/formFields/text-field"
import { ConfigurableDynamicFormFieldsColorPicker } from "src/components/ConfigurableDynamicFormFields/formFields/color-picker"
import { ConfigurableDynamicFormFieldsAutoUuid } from "src/components/ConfigurableDynamicFormFields/formFields/auto-uuid"
import { ConfigurableDynamicFormFieldsBooleanField } from "src/components/ConfigurableDynamicFormFields/formFields/boolean-field"
import { ConfigurableDynamicFormFieldsStore } from "src/components/ConfigurableDynamicFormFields/store"
import { IConfigurableDynamicFormFieldsProps } from "src/components/ConfigurableDynamicFormFields/types/form-field"
import { useStore } from "src/store/lib/useStore"

export const DynamicConfigurableFormField = observer(
    ({ sectionName, field }: IConfigurableDynamicFormFieldsProps) => {
        const store = useStore(ConfigurableDynamicFormFieldsStore)

        // Check if this field has conditional requirements
        if (field.config?.conditional_required != null) {
            const { field_name: fieldName, field_value: fieldValue } =
                field.config.conditional_required

            // Ensure required fields are defined
            if (
                fieldName == null ||
                fieldName === "" ||
                fieldValue === undefined
            ) {
                return null
            }

            const dependentFieldValue = store.getFieldValue(
                sectionName,
                fieldName,
            )

            // Check if the condition is met with type-aware comparison
            let conditionMet = false

            if (typeof fieldValue === "boolean") {
                // For boolean comparisons, ensure both values are properly converted
                conditionMet = Boolean(dependentFieldValue) === fieldValue
            } else {
                // For other types, use strict equality
                conditionMet = dependentFieldValue === fieldValue
            }

            if (!conditionMet) {
                return null
            }
        }

        switch (field.type) {
            case "text":
            case "integer":
            case "textarea":
                return (
                    <ConfigurableDynamicFormFieldsTextField
                        field={field}
                        sectionName={sectionName}
                    />
                )
            case "icon":
                return (
                    <ConfigurableDynamicFormFieldsAssetLibraryPicker
                        activeAssetType="icons"
                        sectionName={sectionName}
                        field={field}
                    />
                )
            case "image":
                return (
                    <ConfigurableDynamicFormFieldsAssetLibraryPicker
                        activeAssetType="images"
                        sectionName={sectionName}
                        field={field}
                    />
                )
            case "text_list":
                return (
                    <ConfigurableDynamicFormFieldsTextList
                        field={field}
                        sectionName={sectionName}
                    />
                )
            case "color":
                return (
                    <ConfigurableDynamicFormFieldsColorPicker
                        field={field}
                        sectionName={sectionName}
                    />
                )
            case "auto_uuid":
                return (
                    <ConfigurableDynamicFormFieldsAutoUuid
                        field={field}
                        sectionName={sectionName}
                    />
                )
            case "boolean":
                return (
                    <ConfigurableDynamicFormFieldsBooleanField
                        field={field}
                        sectionName={sectionName}
                    />
                )
            default:
                // TODO: handle file type, but currently not being used
                return <ConfigurableDynamicFormFieldsUnhandledElementUi />
        }
    },
)
